// In-memory state for the Usage demo: what the presenter clicked in this
// browser session. Shared across components through a window event, like
// the demo flags. Nothing is persisted: a refresh starts from the fixtures.

import { useEffect, useState } from "react";
import type { ActionItemStatus, PersonState } from "./types";

export type Decision = "snoozed" | "auto_renew";

export interface UsageDemoState {
    /** Action items whose status the session changed, by id. */
    itemStatus: Record<string, ActionItemStatus>;
    /** Deals a usage analysis was requested on, in order; each gets a Waiting row. */
    requestedDeals: string[];
    /** Projects with a recorded decision: they leave "On me". */
    decisions: Record<string, Decision>;
    /** Projects a "Refresh usage data" added a revision to. */
    refreshed: Record<string, true>;
    /** Projects a playbook was requested on. */
    playbookRequested: Record<string, true>;
    /** People whose state a bulk action changed, by pseudonymous id. */
    peopleStates: Record<string, PersonState>;
    /** Functions a reminder was sent to in this session. */
    remindersSent: Record<string, true>;
}

const EMPTY_STATE: UsageDemoState = {
    itemStatus: {},
    requestedDeals: [],
    decisions: {},
    refreshed: {},
    playbookRequested: {},
    peopleStates: {},
    remindersSent: {},
};

const EVENT_NAME = "demo.usage.change";

let current: UsageDemoState = EMPTY_STATE;

const writeState = (next: UsageDemoState) => {
    current = next;
    window.dispatchEvent(new CustomEvent(EVENT_NAME, { detail: next }));
};

const update = (fn: (s: UsageDemoState) => UsageDemoState) => writeState(fn(current));

export const useUsageDemoState = (): UsageDemoState => {
    const [state, setState] = useState<UsageDemoState>(current);
    useEffect(() => {
        const onChange = () => setState(current);
        window.addEventListener(EVENT_NAME, onChange);
        return () => window.removeEventListener(EVENT_NAME, onChange);
    }, []);
    return state;
};

export const setItemStatus = (itemId: string, status: ActionItemStatus) =>
    update((s) => ({ ...s, itemStatus: { ...s.itemStatus, [itemId]: status } }));

export const requestUsageAnalysis = (dealId: string) =>
    update((s) =>
        s.requestedDeals.includes(dealId) ? s : { ...s, requestedDeals: [...s.requestedDeals, dealId] }
    );

export const recordDecision = (projectId: string, decision: Decision) =>
    update((s) => ({ ...s, decisions: { ...s.decisions, [projectId]: decision } }));

export const refreshUsageData = (projectId: string) =>
    update((s) => ({ ...s, refreshed: { ...s.refreshed, [projectId]: true } }));

export const requestPlaybook = (projectId: string) =>
    update((s) => ({ ...s, playbookRequested: { ...s.playbookRequested, [projectId]: true } }));

export const setPeopleState = (ids: string[], state: PersonState) =>
    update((s) => ({
        ...s,
        peopleStates: { ...s.peopleStates, ...Object.fromEntries(ids.map((id) => [id, state])) },
    }));

export const sendReminder = (functionName: string) =>
    update((s) => ({ ...s, remindersSent: { ...s.remindersSent, [functionName]: true } }));

/** Back to the fixtures, for the next run of the demo. */
export const resetUsageDemo = () => writeState(EMPTY_STATE);
